import {Routes} from '@angular/router';
import {provideStates} from '@ngxs/store';
import {DashboardState} from './states/dashboard/dashboard.state';
import {DashboardService} from './services/dashboard/dashboard.service';
import {DashboardRedirectComponent} from './components/dashboard-redirect/dashboard-redirect.component';
import {DashboardInsightsComponent} from '../dashboard/containers/dashboard-insights/dashboard-insights.component';
import {NotPhysicianGuard} from '../auth/guards/not-physician/not-physician.guard';
import {InsightsIndexComponent} from '../dashboard/containers/insights/insights-index/insights-index.component';
import {BrainAgeComponent} from '../dashboard/containers/insights/brain-age/brain-age.component';
import {BrainResearchComponent} from '../dashboard/containers/insights/brain-research/brain-research.component';
import {UPLOAD_SCAN_INITIATOR} from '../upload-scan/symbols';
import {PhysicianGuard} from '../auth/guards/physician/physician.guard';
import {ImagingCenterGuard} from '../auth/guards/imaging-center/imaging-center.guard';
import {DashboardGeneticsSingleViewComponent} from '../dashboard-genetics/components/dashboard-genetics-single-view/dashboard-genetics-single-view.component';
import {dashboardStudyRoutes} from '../dashboard-study/dashboard-study.routes';
import {DashboardStudyViewerState} from '../dashboard-study/states/dashboard-study-viewer/dashboard-study-viewer.state';

export const dashboardRoutes: Routes = [
  {
    // On the dashboard init we don't know yet which page to show.
    // We need to check the user permissions and redirect to the right page.
    path: '',
    component: DashboardRedirectComponent,
  },
  {
    path: 'user/:userUid',
    loadComponent: () => import('./components/dashboard-user/dashboard-user.component').then(m => m.DashboardUserComponent),
    providers: [DashboardService, provideStates([DashboardState, DashboardStudyViewerState])],
    children: dashboardStudyRoutes,
  },
  {
    path: 'static-images',
    loadChildren: () => import('../dashboard-static-images/dashboard-static-images.module').then(m => m.DashboardStaticImagesModule),
  },
  {
    path: 'insights',
    component: DashboardInsightsComponent,
    data: {
      title: '3D Brain Model',
      isInsightsPage: true,
    },
    canActivate: [NotPhysicianGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: InsightsIndexComponent,
        data: {
          title: '3D Brain Model',
        },
      },
      {
        path: 'brain-age',
        component: BrainAgeComponent,
        data: {
          title: '3D Brain Model',
        },
      },
      {
        path: 'brain-research',
        component: BrainResearchComponent,
        data: {
          title: '3D Brain Model',
        },
      },
      {
        path: 'questionnaire',
        loadChildren: () => import('../dashboard/modules/questionnaire/questionnaire.module').then(m => m.QuestionnaireModule),
      },
      {
        path: 'safety-screening',
        loadChildren: () =>
          import('../dashboard-safety-screening/dashboard-safety-screening.module').then(m => m.DashboardSafetyScreeningModule),
      },
    ],
  },
  {
    path: 'profile',
    loadChildren: () => import('../dashboard/modules/profile/profile.module').then(m => m.ProfileModule),
    data: {
      title: '3D Brain Model',
    },
  },
  {
    path: 'print-brain',
    loadChildren: () => import('../dashboard-print-brain/dashboard-print-brain.module').then(m => m.DashboardPrintBrainModule),
    data: {
      title: '3D Brain Model',
    },
  },
  {
    path: 'request-records',
    loadChildren: () => import('../dashboard/modules/request-records/request-records.module').then(m => m.RequestRecordsModule),
    data: {
      title: '3D Brain Model',
    },
  },
  {
    path: 'upload-scan',
    loadChildren: () => import('../upload-scan/upload-scan.module').then(m => m.UploadScanModule),
    data: {
      title: 'Upload Scan',
      flowInitiator: UPLOAD_SCAN_INITIATOR.USER,
    },
    canActivate: [NotPhysicianGuard],
  },
  {
    path: 'brainkey-report',
    loadChildren: () => import('../dashboard/modules/brainkey-report/brainkey-report.module').then(m => m.BrainkeyReportModule),
    data: {
      title: '3D Brain Model',
    },
  },
  {
    path: 'add-patient',
    loadChildren: () => import('../upload-scan/upload-scan.module').then(m => m.UploadScanModule),
    data: {
      title: 'Add Patient',
      flowInitiator: UPLOAD_SCAN_INITIATOR.PHYSICIAN,
    },
    canActivate: [PhysicianGuard],
  },
  {
    path: 'imaging-center',
    loadChildren: () => import('../dashboard/modules/imaging-center/imaging-center.module').then(m => m.ImagingCenterModule),
    data: {
      title: 'Imaging Center',
    },
    canActivate: [ImagingCenterGuard],
  },
  {
    path: 'clinic-patients',
    loadChildren: () => import('../dashboard-clinic-patients/dashboard-clinic-patients.module').then(m => m.DashboardClinicPatientsModule),
    data: {
      title: 'Imaging Center',
    },
    canActivate: [PhysicianGuard],
  },
  {
    path: 'clinic-physicians',
    loadChildren: () => import('../dashboard-clinic-staff/dashboard-clinic-staff.module').then(m => m.DashboardClinicStaffModule),
    data: {
      title: 'Imaging Center',
    },
    canActivate: [PhysicianGuard],
  },
  {
    path: 'single-view-genetics',
    component: DashboardGeneticsSingleViewComponent,
    data: {
      title: '3D Brain Model',
    },
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];
